import { I18n } from "react-redux-i18n";
import React from "react";

import { FolderPrimaryListDto } from "@common/dto/folder/primary-list-dto";
import { HttpErrorDto } from "@common/dto/http";

import { QueryCreatingProps } from "./query-creating.types";
import { SkipLogicCondition } from "@common/components/skip-logic/skip-logic.types";
import { generateId } from "@common/utils/common";
import { FieldPrimaryListDto } from "@common/dto/field/list-primary-dto";

export const useQueryCreatingController = (props: QueryCreatingProps) => {
  const { showErrorMessage, getFolders, getFolderFields, showConfirmMessage } = props;

  const [isFoldersLoading, setIsFoldersLoading] = React.useState(false);
  const [isFolderFieldsLoading, setIsFolderFieldsLoading] = React.useState(false);

  const [searchKeyword, setSearchKeyword] = React.useState("");

  const [selectedFolder, setSelectedFolder] = React.useState<FolderPrimaryListDto>();
  const [selectedFolderFields, setSelectedFolderFields] = React.useState<Array<FieldPrimaryListDto>>([]);
  const [queryBuilderConditions, setQueryBuilderConditions] = React.useState<Array<SkipLogicCondition>>([]);

  const [folders, setFolders] = React.useState<FolderPrimaryListDto[]>([]);
  const [folderFields, setFolderFields] = React.useState<Array<FieldPrimaryListDto>>([]);

  const handleGetFolders = React.useCallback(async () => {
    try {
      setIsFoldersLoading(true);
      const folders = await getFolders();
      setFolders(folders);
    } catch (err) {
      console.error(err);

      const errorMessage = (err as HttpErrorDto)?.entity || I18n.t("Failed to get query management folders");
      showErrorMessage(errorMessage);
    } finally {
      setIsFoldersLoading(false);
    }
  }, [showErrorMessage, getFolders]);

  const handleQueryBuilderChange = React.useCallback((conditions: SkipLogicCondition[]) => {
    setQueryBuilderConditions(conditions);
  }, []);

  const handleAllConditionsRemove = React.useCallback(() => {
    showConfirmMessage({
      cancelText: I18n.t("No"),
      confirmText: I18n.t("Yes"),
      confirmBtnId: "btn-message-box-ok",
      cancelBtnId: "btn-message-box-dont-cancel",
      content: I18n.t("Are you sure you wish to remove all query clauses?"),
      onCustomOk: () => {
        setQueryBuilderConditions([]);
      },
    });
  }, [showConfirmMessage]);

  const handleGetFolderFields = React.useCallback(
    async (params: { folderId?: FolderPrimaryListDto["id"]; searchQuery?: string }) => {
      try {
        setIsFolderFieldsLoading(true);
        const folderFields = await getFolderFields(params);
        setFolderFields(folderFields);
      } catch (err) {
        console.error(err);

        const errorMessage = (err as HttpErrorDto)?.entity || I18n.t("Failed to get query management folder fields");
        showErrorMessage(errorMessage);
      } finally {
        setIsFolderFieldsLoading(false);
      }
    },
    [showErrorMessage, getFolderFields]
  );

  React.useEffect(() => {
    handleGetFolders();
  }, []);

  const handleSearchChange = React.useCallback(
    ({ searchKeyword }: { searchKeyword: string }) => {
      setSearchKeyword(searchKeyword);
      setSelectedFolderFields([]);
      setSelectedFolder(undefined);
      handleGetFolderFields({ searchQuery: searchKeyword });
    },
    [handleGetFolderFields]
  );

  const handleSelectedFolderFieldChange = React.useCallback((folderFields: Array<FieldPrimaryListDto>) => {
    setSelectedFolderFields(folderFields);
  }, []);

  const handleSelectedFolderChange = React.useCallback(
    (folder: FolderPrimaryListDto) => {
      setSelectedFolder(folder);
      handleGetFolderFields({ searchQuery: searchKeyword, folderId: folder.id });
    },
    [handleGetFolderFields, searchKeyword]
  );

  const handleFolderFieldDrag = React.useCallback(
    (item: FieldPrimaryListDto) => {
      const data: SkipLogicCondition[] = selectedFolderFields.map((folderField) => ({
        id: generateId(),
        field: folderField,
        leftParenthesis: false,
        rightParenthesis: false,
      }))

      setQueryBuilderConditions([...queryBuilderConditions, ...data]);
    },
    [selectedFolderFields, queryBuilderConditions]
  );

  return {
    folders,
    folderFields,
    isFoldersLoading,
    isFolderFieldsLoading,
    selectedFolder,
    searchKeyword,
    selectedFolderFields,
    handleSearchChange,
    handleSelectedFolderChange,
    handleSelectedFolderFieldChange,
    handleFolderFieldDrag,
    queryBuilderConditions,
    handleQueryBuilderChange,
    handleAllConditionsRemove
  };
};
